import { AppProvider, useApp } from './lib/AppContext';
import { AuthProvider } from './lib/AuthContext';
import { AdminProvider, useAdmin } from './lib/AdminContext';
import { PreviewModeProvider } from './lib/PreviewModeContext';
import { ExplorerProvider } from './lib/ExplorerContext';
import { useOnboarding } from './lib/useOnboarding';
import { OnboardingSync } from './lib/OnboardingSync';
import { LastSeenTracker } from './lib/LastSeenTracker';
import { HeartbeatTracker } from './lib/HeartbeatTracker';
import { EmailVerificationTracker } from './lib/EmailVerificationTracker';
import { PasswordRecoveryHandler } from './lib/PasswordRecoveryHandler';
import Onboarding from './screens/Onboarding';
import Home from './screens/Home';
import Explore from './screens/Explore';
import CardDetail from './screens/CardDetail';
import Saved from './screens/Saved';
import ProfileComplete from './screens/ProfileComplete';
import Settings from './screens/Settings';
import ProfileSettings from './screens/ProfileSettings';
import ChangeEmail from './screens/ChangeEmail';
import ChangePassword from './screens/ChangePassword';
import DeleteAccount from './screens/DeleteAccount';
import IssuePage from './screens/IssuePage';
import EditInterests from './screens/EditInterests';
import ContributeRoot from './screens/ContributeRoot';
import SubmitSong from './screens/SubmitSong';
import SubmissionSuccess from './screens/SubmissionSuccess';
import Feedback from './screens/Feedback';
import Auth from './screens/Auth';
import ResetPassword from './screens/ResetPassword';
import UploadAlbumArt from './screens/UploadAlbumArt';
import AdminSongs from './screens/AdminSongs';
import AdminWeeklyCardsOverview from './screens/AdminWeeklyCardsOverview';
import AdminWeeklyCardsEditLive from './screens/AdminWeeklyCardsEditLive';
import AdminWeeklyCardsCreateEdit from './screens/AdminWeeklyCardsCreateEdit';
import AdminWeeklyCardsViewPublished from './screens/AdminWeeklyCardsViewPublished';
import AdminUsers from './screens/AdminUsers';
import AdminAnalytics from './screens/AdminAnalytics';
import SongEditorUnified from './screens/SongEditorUnifiedNew';
import BottomNav from './components/BottomNav';
import PreviewBar from './components/PreviewBar';
import Toast from './components/Toast';
import ReflectionPrompt from './components/ReflectionPrompt';
import ErrorBoundary from './ErrorBoundary';

function AppContent() {
  const { hasCompletedOnboarding } = useOnboarding();
  const { currentScreen, showToast, navigateTo } = useApp();
  const { isAdmin: userIsAdmin, viewAsUser } = useAdmin();

  console.log('[AppContent] Render - hasCompletedOnboarding:', hasCompletedOnboarding, 'currentScreen:', currentScreen);

  // Always render trackers, even during onboarding
  if (!hasCompletedOnboarding) {
    console.log('[AppContent] Showing Onboarding screen');
    return (
      <>
        <EmailVerificationTracker />
        <PasswordRecoveryHandler />
        <Onboarding />
      </>
    );
  }

  console.log('[AppContent] Onboarding complete, showing:', currentScreen);

  const adminScreens = ['admin-songs', 'admin-weekly-cards', 'admin-weekly-cards-edit-live', 'admin-weekly-cards-create', 'admin-weekly-cards-edit', 'admin-weekly-cards-view-published', 'admin-users', 'admin-analytics', 'song-editor'];
  const isAdminScreen = adminScreens.includes(currentScreen);

  // If in preview mode and trying to access admin screens, redirect to home
  if (viewAsUser && isAdminScreen) {
    console.log('[AppContent] Preview mode active, redirecting to home from:', currentScreen);
    navigateTo('home');
    return null;
  }

  if (isAdminScreen && !userIsAdmin) {
    console.log('[AppContent] Access denied to admin screen:', currentScreen, 'userIsAdmin:', userIsAdmin);
    showToast('Access denied. Admin privileges required.');
    navigateTo('home');
    return null;
  }

  const showBottomNav = ![
    'settings',
    'profile-settings',
    'change-email',
    'change-password',
    'delete-account',
    'issue-page',
    'edit-interests',
    'auth',
    'reset-password',
    'upload-album-art',
    ...adminScreens,
  ].includes(currentScreen);

  return (
    <div className="relative">
      <PreviewBar />
      <div className={viewAsUser ? 'pt-[52px]' : ''}>
        <OnboardingSync />
        <LastSeenTracker />
        <HeartbeatTracker />
        <EmailVerificationTracker />
        <PasswordRecoveryHandler />
        {currentScreen === 'home' && <Home />}
        {currentScreen === 'explore' && <Explore />}
        {currentScreen === 'card-detail' && <CardDetail />}
        {currentScreen === 'saved' && <Saved />}
        {currentScreen === 'profile' && <ProfileComplete />}
        {currentScreen === 'settings' && <Settings />}
        {currentScreen === 'profile-settings' && <ProfileSettings />}
        {currentScreen === 'change-email' && <ChangeEmail />}
        {currentScreen === 'change-password' && <ChangePassword />}
        {currentScreen === 'delete-account' && <DeleteAccount />}
        {currentScreen === 'issue-page' && <IssuePage />}
        {currentScreen === 'edit-interests' && <EditInterests />}
        {currentScreen === 'contribute' && <ContributeRoot />}
        {currentScreen === 'submit-song' && <SubmitSong />}
        {currentScreen === 'submission-success' && <SubmissionSuccess />}
        {currentScreen === 'feedback' && <Feedback />}
        {currentScreen === 'auth' && <Auth />}
        {currentScreen === 'reset-password' && <ResetPassword />}
        {currentScreen === 'upload-album-art' && <UploadAlbumArt />}
        {currentScreen === 'admin-songs' && <AdminSongs />}
        {currentScreen === 'admin-weekly-cards' && <AdminWeeklyCardsOverview />}
        {currentScreen === 'admin-weekly-cards-edit-live' && <AdminWeeklyCardsEditLive />}
        {currentScreen === 'admin-weekly-cards-create' && <AdminWeeklyCardsCreateEdit />}
        {currentScreen === 'admin-weekly-cards-edit' && <AdminWeeklyCardsCreateEdit />}
        {currentScreen === 'admin-weekly-cards-view-published' && <AdminWeeklyCardsViewPublished />}
        {currentScreen === 'admin-users' && <AdminUsers />}
        {currentScreen === 'admin-analytics' && <AdminAnalytics />}
        {currentScreen === 'song-editor' && (() => {
          console.log('[AppContent] Rendering SongEditorUnified');
          return <SongEditorUnified />;
        })()}
        {showBottomNav && <BottomNav />}
        <Toast />
        <ReflectionPrompt />
      </div>
    </div>
  );
}

export default function App() {
  console.log('[App] Rendering App component');
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ExplorerProvider>
          <AppProvider>
            <AdminProvider>
              <PreviewModeProvider>
                <AppContent />
              </PreviewModeProvider>
            </AdminProvider>
          </AppProvider>
        </ExplorerProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
